import { useCallback, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import type { IWebsiteAnalysisTrafficRequestParams } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { RankByMode } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import DurationServiceInstance from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { RootState } from "single-spa/store/types";

import { TopPageWidgetsRowWrap } from "../StyledComponents";

import { getCountryText } from "./common/getCountryText";
import { ConversionRate } from "./ConversionRate/ConversionRate";
import { DeviceDistribution } from "./DeviceDistribution/DeviceDistribution";
import { useCompareDeviceDistributionData } from "./DeviceDistribution/useCompareDeviceDistributionData";
import { TotalVisits } from "./TotalVisits/TotalVisits";
import { useCompareTotalVisitsData } from "./TotalVisits/useCompareTotalVisitsData";
import { useCompareWebRanksData } from "./WebsiteRank/useCompareWebRanksData";
import { WebsiteRank } from "./WebsiteRank/WebsiteRank";
import { getWebsiteRankSubtitleFilters } from "./WebsiteRank/websiteRankSubtitleFilters";

export const WWOTrafficEngagementWidgets = () => {
    const showGAApprovedData = useSelector((state: RootState) => state.common.showGAApprovedData as boolean);
    const showBetaBranchData = useSelector((state: RootState) => state.common.showBetaBranchData.value as boolean);
    const swConnectedAccountsService = useMemo(() => connectedAccountsServiceFactory(), []);
    const { params } = useCurrentStateAndParams();
    const rawParams = params as Record<string, string>;
    const initialCountry = rawParams.country ?? "";
    const { apiController, isWWWithoutPermissions } = websiteWorldWideOverviewLogic(initialCountry);
    const effectiveDuration = isWWWithoutPermissions ? "3m" : (rawParams.duration ?? "");
    const comparedDuration = rawParams.comparedDuration ?? "";
    const durationObject = useMemo(
        () => DurationServiceInstance.getDurationData(effectiveDuration, comparedDuration),
        [comparedDuration, effectiveDuration],
    );

    const paramsForApi = useMemo(
        () => ({ ...rawParams, duration: effectiveDuration }),
        [initialCountry, effectiveDuration, comparedDuration, rawParams.key, rawParams.webSource, rawParams.isWWW],
    );

    const apiParams = useMemo<Record<string, string>>(() => {
        const responseParams = proNavigator.getApiParams(paramsForApi) as Record<string, string>;
        const compareFrom = durationObject.forAPI.compareFrom;
        const compareTo = durationObject.forAPI.compareTo;
        return {
            ...responseParams,
            compareFrom,
            compareTo,
            duration: effectiveDuration,
        };
    }, [effectiveDuration, durationObject, paramsForApi]);

    const webSource = apiParams.webSource ?? "";
    const country = apiParams.country ?? "";
    const key = apiParams.key ?? "";
    const isWindow = apiParams.isWindow === "true";
    const isWWW = !!apiParams.isWWW;
    const to = apiParams.to ?? "";
    const from = apiParams.from ?? "";
    const latest = apiParams.latest ?? "";
    const apiParamsObj = useMemo(() => {
        return {
            country,
            from,
            to,
            includeSubDomains: !isWWW,
            isWindow,
            keys: key,
            timeGranularity: "Monthly",
            webSource,
            ...(latest ? { latest } : {}),
        };
    }, [country, from, to, isWWW, isWindow, key, webSource, latest]) as IWebsiteAnalysisTrafficRequestParams;

    const compareApiParams = useMemo(() => {
        return {
            ...apiParamsObj,
            ["ShouldGetVerifiedData"]: showGAApprovedData,
            beta: !!showBetaBranchData,
        };
    }, [apiParamsObj, showGAApprovedData, showBetaBranchData]) as IWebsiteAnalysisTrafficRequestParams;

    const totalVisitsMetricData = useCompareTotalVisitsData(compareApiParams, {
        controller: apiController,
    });

    const devicesDistributionMetricData = useCompareDeviceDistributionData(compareApiParams, {
        controller: apiController,
    });

    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                },
                href: "",
            },
            {
                filter: "country",
                countryCode: country,
                value: getCountryText(country),
                href: "",
            },
        ],
        [country, durationObject],
    );

    const [selectedRankByMode, setSelectedRankByMode] = useState<RankByMode>(RankByMode.Country);

    const webRanksData = useCompareWebRanksData(apiParamsObj, selectedRankByMode, {
        controller: apiController,
    });
    const isWebRanksLoading = webRanksData.isLoading || webRanksData.isFetching;

    const getRedirectUrl = useCallback((redirectCountry: string, category: string) => {
        if (proNavigator.isSalesSolution()) {
            return undefined;
        }
        return proNavigator.getStateUrl("digitalsuite_marketresearch_webmarketanalysis_mapping", {
            country: redirectCountry,
            duration: "1m",
            category,
        });
    }, []);

    const rankSubtitleFilters = useMemo(() => {
        return getWebsiteRankSubtitleFilters({
            data: webRanksData.data,
            selectedRankByMode,
            subtitleFilters,
            defaultCountry: country,
            getRedirectUrl,
        });
    }, [country, getRedirectUrl, selectedRankByMode, subtitleFilters, webRanksData.data]);

    const onRankModeChange = useCallback((item: { id: RankByMode }) => {
        setSelectedRankByMode(item?.id);
    }, []);

    const isDevicesDistributionLoading =
        devicesDistributionMetricData.isLoading || devicesDistributionMetricData.isFetching;

    const isTotalVisitsLoading = totalVisitsMetricData.isLoading || totalVisitsMetricData.isFetching;

    const conversionRateParams = useMemo(
        () => ({
            country: Number(country),
            from,
            to,
            key,
            isWindow,
            isWWW,
        }),
        [country, from, to, key, isWindow, isWWW],
    );

    const isGAPrivate = useMemo(
        () => swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(apiParamsObj.keys),
        [apiParamsObj.keys, swConnectedAccountsService],
    );

    return (
        <TopPageWidgetsRowWrap>
            <TotalVisits
                data={totalVisitsMetricData.data}
                subtitleFilters={subtitleFilters}
                hasGA={showGAApprovedData}
                isGAPrivate={isGAPrivate}
                isLoading={isTotalVisitsLoading}
                params={paramsForApi}
                showBetaBranchData={showBetaBranchData}
                showA2d={false}
            />
            <ConversionRate params={conversionRateParams} />
            <DeviceDistribution
                data={devicesDistributionMetricData.data}
                subtitleFilters={subtitleFilters}
                hasGA={showGAApprovedData}
                isGAPrivate={isGAPrivate}
                isLoading={isDevicesDistributionLoading}
                showBetaBranchData={showBetaBranchData}
                showA2d={false}
                params={apiParams}
            />
            <WebsiteRank
                data={webRanksData.data}
                subtitleFilters={rankSubtitleFilters}
                onRankModeChange={onRankModeChange}
                selectedRankMode={selectedRankByMode}
                isLoading={isWebRanksLoading}
            />
        </TopPageWidgetsRowWrap>
    );
};
